import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import { STEP_LABELS, STEPS, WIZARD_DEFINITION } from './steps';
import { FulfillmentProductImport } from './steps/create-products/fulfillment-product-import';
import style from './style.module.less';
import { useFulfillmentFTUWizardState } from './use-fulfillment-ftu-wizard-state';
import { Stepper } from '../../../../components/stepper';
import { useScreenSizeContext } from '../../../../contexts/screen-size-resolution';
import { BASE_FULFILLMENTS_PAGE_PATH, FULFILLMENT_RESOURCES_PATH } from '../../../../routes/constants';
import { LogService } from '../../../../services/log-service';

/** @typedef {import("./types").StepDefinition} StepDefinition */

/**
 * @param {string} initialStep
 * @returns {{ activeStepId: string, goToNext: () => string, goToPrevious: () => string }}
 */
const useNavigation = (initialStep) => {
  const [activeStepId, setActiveStepId] = useState(
    () => WIZARD_DEFINITION.find((step) => step.id === initialStep)?.id ?? WIZARD_DEFINITION[0].id,
  );

  /**
   * @returns {number}
   */
  const getStepIndex = () => {
    const index = WIZARD_DEFINITION.findIndex((step) => step.id === activeStepId);
    if (index === -1) {
      LogService.logWarning('Fulfillment FTU navigation warning', {
        incorrectStepId: activeStepId,
        validStepIds: WIZARD_DEFINITION.map((step) => step.id),
      });
      return 0;
    }
    return index;
  };

  const goToNext = () => {
    const activeStepIndex = getStepIndex();
    const nextStepId = WIZARD_DEFINITION[activeStepIndex + 1].id;
    setActiveStepId(nextStepId);

    return nextStepId;
  };

  const goToPrevious = () => {
    const activeStepIndex = getStepIndex();
    const previousStepId = WIZARD_DEFINITION[activeStepIndex - 1].id;
    setActiveStepId(previousStepId);

    return previousStepId;
  };

  return {
    activeStepId,
    goToNext,
    goToPrevious,
  };
};

const DRAWER_POSITION_RIGHT = 'right';
const DRAWER_SIZE_LARGE = 'large';

/**
 * FTU Wizard component for fulfillments overview
 * @param {object} props
 * @param {() => void} props.onClose
 * @returns {React.JSX.Element}
 */
export const FulfilledByShipstationFTUWizard = ({ onClose }) => {
  const { initialStep, wizardState, updateWizardStep, saveUserActiveStep, completeWizard } = useFulfillmentFTUWizardState();
  const navigate = useNavigate();

  const { activeStepId, goToNext, goToPrevious } = useNavigation(initialStep);

  const { isMobile, isUltraMobile } = useScreenSizeContext();

  useEffect(() => {
    if (!wizardState.hasStarted) {
      saveUserActiveStep(activeStepId);
    }
  }, [wizardState.hasStarted, activeStepId, saveUserActiveStep]);

  const onGoToNext = () => {
    const stepId = goToNext();
    saveUserActiveStep(stepId);
  };

  const onGoToPrev = () => {
    const stepId = goToPrevious();
    saveUserActiveStep(stepId);
  };

  const onCompleteSetup = () => {
    completeWizard();
    onClose();
    navigate(`${BASE_FULFILLMENTS_PAGE_PATH}/${FULFILLMENT_RESOURCES_PATH}`);
  };

  const activeStep = /** @type {StepDefinition} */ (WIZARD_DEFINITION.find((step) => step.id === activeStepId));

  const stepProps = wizardState.steps[activeStepId];

  return (
    <Drawer
      canvasClassName={style['drawer-canvas']}
      onCancel={onClose}
      onClose={onClose}
      position={DRAWER_POSITION_RIGHT}
      shouldNotUsePortal
      size={DRAWER_SIZE_LARGE}
    >
      <DrawerHeader
        className={style['ftu-drawer-header']}
        closeButtonAriaLabel={messages.closeDrawerButtonAriaLabel}
        onClose={onClose}
        title={<Text textType={TextType.H4} value={messages.drawerHeaderText} />}
      />
      <FulfillmentProductImport>
        <div className={style['ftu-wizard-pane']}>
          <FlexColumn className={style['ftu-wizard-sidebar']}>
            <FlexRow className={style['ftu-wizard-stepper']}>
              <Stepper
                currentStep={WIZARD_DEFINITION.indexOf(activeStep) + 1}
                isHorizontal={isMobile || isUltraMobile}
                stepLabels={STEP_LABELS}
                totalSteps={STEP_LABELS.length}
              />
            </FlexRow>
          </FlexColumn>
          <div className={style['ftu-wizard-content']}>
            <div className={style['ftu-wizard-step']}>
              <Text className={style['ftu-wizard-step-title']} textType={TextType.H3} value={activeStep.title} />
              <Text className={style['ftu-wizard-step-description']} textType={TextType.Paragraph} value={activeStep.description} />
              <div className={style['ftu-wizard-step-body']}>
                <activeStep.Body {...stepProps} updateWizardStep={updateWizardStep} />
              </div>
            </div>
          </div>
        </div>
        <FlexRow className={style['ftu-wizard-footer']}>
          <activeStep.Footer
            {...stepProps}
            goToNext={activeStepId === STEPS.scheduleReview ? onCompleteSetup : onGoToNext}
            goToPrevious={onGoToPrev}
          />
        </FlexRow>
      </FulfillmentProductImport>
    </Drawer>
  );
};
